import React, { Component } from 'react';
import { any, array, arrayOf, bool, number, object, oneOfType, shape, string } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, intlShape } from '../../util/reactIntl';
import { metaTagProps } from '../../util/seo';
import { alternateHreflangs, canonicalRoutePath, createResourceLocatorString } from '../../util/routes';
import { getLocaleFromUrl } from '../../constants/locales';
import { propTypes } from '../../util/types';
import CookieConsent from '../CookieConsent/CookieConsent';

import css from './Page.module.css';
import { useSelector } from 'react-redux';
import usePartnerStyleLoader from '../../hooks/usePartnerStyleLoader';
import ListingService from '../../services/ListingService';
import { getTranslatedListingType, createURLParamsForListing, createLocationFromAddressParts } from '../../util/listingHelpers';

const preventDefault = e => {
  e.preventDefault();
};

const twitterPageURL = siteTwitterHandle => {
  if (siteTwitterHandle && siteTwitterHandle.charAt(0) === '@') {
    return `https://twitter.com/${siteTwitterHandle.substring(1)}`;
  } else if (siteTwitterHandle) {
    return `https://twitter.com/${siteTwitterHandle}`;
  }
  return null;
};

const normalizeSchemaItem = schemaItem => {
  if (!schemaItem) {
    return [];
  }

  if (typeof schemaItem === 'string') {
    try {
      const parsed = JSON.parse(schemaItem);
      return normalizeSchemaInput(parsed);
    } catch (e) {
      return [];
    }
  }

  if (typeof schemaItem !== 'object') {
    return [];
  }

  return [{ ...schemaItem, '@context': schemaItem['@context'] || 'https://schema.org' }];
};

const normalizeSchemaInput = schemaInput => {
  if (!schemaInput) {
    return [];
  }

  return (Array.isArray(schemaInput) ? schemaInput : [schemaInput]).reduce(
    (schemas, schemaItem) => schemas.concat(normalizeSchemaItem(schemaItem)),
    []
  );
};

class PageComponent extends Component {
  constructor(props) {
    super(props);
    // Keeping scrollPosition out of state reduces rendering cycles (and no bad states rendered)
    this.scrollPosition = 0;
    this.contentDiv = null;
    this.scrollingDisabledChanged = this.scrollingDisabledChanged.bind(this);
  }

  componentDidMount() {
    // By default a dropped file is loaded in the browser window as a
    // file URL. We want to prevent this since it might loose a lot of
    // data the user has typed but not yet saved. Preventing requires
    // handling both dragover and drop events.
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    // Remove duplicated server-side rendered page schema.
    // It's in <body> to improve initial rendering performance,
    // but after web app is initialized, react-helmet-async operates with <head>
    // const pageSchema = document.getElementById('page-schema');
    // if (pageSchema) {
    //   pageSchema.remove();
    // }
  }

  componentWillUnmount() {
    document.removeEventListener('dragover', preventDefault);
    document.removeEventListener('drop', preventDefault);
  }

  scrollingDisabledChanged(currentScrollingDisabled) {
    if (currentScrollingDisabled && currentScrollingDisabled !== this.scrollingDisabled) {
      // Update current scroll position, if scrolling is disabled (e.g. modal is open)
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      this.scrollingDisabled = currentScrollingDisabled;
    } else if (currentScrollingDisabled !== this.scrollingDisabled) {
      this.scrollingDisabled = currentScrollingDisabled;
    }
  }

  render() {
    const {
      className,
      rootClassName,
      children,
      location,
      intl,
      scrollingDisabled,
      referrer,
      author,
      openGraphType,
      description,
      facebookImages,
      published,
      schema,
      socialSharing,
      title,
      twitterHandle,
      twitterImages,
      updated,
      config,
      routeConfiguration,
      stPagesData,
      styles,
      // Passed on the ListingPage so we can
      // get the alternate listing titles for alternateHreflangs
      listing,
      seoUrlsInAllLang,
      shouldNoindex
    } = this.props;

    const classes = classNames(rootClassName || css.root, className, {
      [css.scrollingDisabled]: scrollingDisabled,
    });

    this.scrollingDisabledChanged(scrollingDisabled);

    const { marketplaceRootURL, marketplaceName } = config;
    const shouldReturnPathOnly = referrer && referrer !== 'unsafe-url';
    
    // Get the actual locale from the URL to fix SSR locale issues
    const actualLocale = getLocaleFromUrl(location.pathname) || intl.locale;
    
    // For listing pages, generate the correct canonical path from listing data
    // This ensures we always use the new slug format (generated from listing data)
    // instead of preserving whatever slug was in the URL
    let canonicalPath;
    if (listing && listing.id) {
      const pathParams = createURLParamsForListing(listing, actualLocale, intl);
      const listingPath = createResourceLocatorString('ListingPage', routeConfiguration, pathParams, {});
      canonicalPath = shouldReturnPathOnly ? listingPath : `${listingPath}${location.search}${location.hash}`;
    } else {
      canonicalPath = canonicalRoutePath(routeConfiguration, location, shouldReturnPathOnly);
    }

    // For SEO pages with localized URLs, use the localized path for canonical URL
    if (seoUrlsInAllLang && actualLocale) {
      // Check if we actually have valid localized URLs
      const hasValidLocalizedUrls = Object.keys(seoUrlsInAllLang).length > 0 && 
        Object.values(seoUrlsInAllLang).some(url => url && url.trim() !== '');
      
      if (hasValidLocalizedUrls) {
        const localizedPath = seoUrlsInAllLang[actualLocale];
        if (localizedPath) {
          // Construct the localized canonical path
          // Ensure localizedPath doesn't start with '/' and add locale prefix
          const cleanPath = localizedPath.startsWith('/') ? localizedPath.slice(1) : localizedPath;
          canonicalPath = shouldReturnPathOnly 
            ? `/${actualLocale}/${cleanPath}`
            : `/${actualLocale}/${cleanPath}`;
        } else {
          // Fallback: if current locale not found in mappings, ensure URL still uses current locale
          const urlParts = canonicalPath.split('/');
          if (urlParts.length >= 2 && actualLocale !== urlParts[1]) {
            urlParts[1] = actualLocale;
            canonicalPath = urlParts.join('/');
          }
        }
      } else {
        // Fallback: ensure the canonical path uses the current locale from the URL
        const urlParts = canonicalPath.split('/');
        if (urlParts.length >= 2 && actualLocale !== urlParts[1]) {
          urlParts[1] = actualLocale;
          canonicalPath = urlParts.join('/');
        }
      }
    } else {
      // Fallback: ensure the canonical path uses the current locale from the URL
      // This handles cases where seoUrlsInAllLang is not available
      const urlParts = canonicalPath.split('/');
      if (urlParts.length >= 2 && actualLocale !== urlParts[1]) {
        urlParts[1] = actualLocale;
        canonicalPath = urlParts.join('/');
      }
    }

    const listingMultiLangTitles = listing
      ? listing.attributes?.publicData?.titles ||
      ListingService.createMultiLangTitles(intl, listing)
      : null;

    const listingMultiLangActivity = listing
      ? listing.attributes?.publicData?.activityTranslated ||
      ListingService.createMultiLangActivity(listing.attributes.publicData.activity, config)
      : null;

    const { type: listingType } = listing?.attributes?.publicData || {};

    const newListingMultiLangTitles = {
      "english": ListingService.createTitle(intl, listing, "english"),
      "french": ListingService.createTitle(intl, listing, "french"),
      "german": ListingService.createTitle(intl, listing, "german"),
      "italian": ListingService.createTitle(intl, listing, "italian"),
    }

    const newListingMultiLangTypes = {
      "english": getTranslatedListingType(listingType, "en-US"),
      "french": getTranslatedListingType(listingType, "fr-FR"),
      "german": getTranslatedListingType(listingType, "de-DE"),
      "italian": getTranslatedListingType(listingType, "it-IT"),
    }

    const altHreflangs = alternateHreflangs(
      canonicalPath,
      stPagesData,
      newListingMultiLangTitles,
      listingMultiLangActivity,
      listing?.attributes?.publicData?.multiLangAddressParts,
      intl,
      newListingMultiLangTypes,
      seoUrlsInAllLang
    );

    const canonicalUrl = `${marketplaceRootURL}${canonicalPath}`;

    // const schemaTitle = intl.formatMessage(
    //   { id: 'Page.schemaTitle' },
    //   { siteTitle: marketplaceName }
    // );
    // const schemaDescription = intl.formatMessage({ id: 'Page.schemaDescription' });
    const pageTitle = title;
    const pageDescription = description;
    const {
      title: socialSharingTitle,
      description: socialSharingDescription,
      images1200: socialSharingImages1200,
      // Note: we use image with open graph's aspect ratio (1.91:1) also with Twitter
      images600: socialSharingImages600,
    } = socialSharing || {};

    // Images for social media sharing
    const defaultFacebookImageURL = config.branding.facebookImage;
    const openGraphFallbackImages = [
      {
        name: 'facebook',
        url: defaultFacebookImageURL,
        width: 1200,
        height: 630,
      },
    ];
    const defaultTwitterImageURL = config.branding.twitterImage;
    const twitterFallbackImages = [
      {
        name: 'twitter',
        url: defaultTwitterImageURL,
        width: 600,
        height: 314,
      },
    ];
    const facebookImgs = socialSharingImages1200 || facebookImages || openGraphFallbackImages;
    const twitterImgs = socialSharingImages600 || twitterImages || twitterFallbackImages;

    const metaToHead = metaTagProps(
      {
        author,
        openGraphType,
        socialSharingTitle: socialSharingTitle || pageTitle,
        socialSharingDescription: socialSharingDescription || pageDescription,
        description: pageDescription,
        facebookImages: facebookImgs,
        twitterImages: twitterImgs,
        twitterHandle,
        published,
        updated,
        url: canonicalUrl,
        locale: actualLocale, // Use actualLocale instead of intl.locale
      },
      config
    );

    const facebookPage = config.siteFacebookPage;
    const twitterPage = twitterPageURL(config.siteTwitterHandle);
    const instagramPage = config.siteInstagramPage;
    const linkedInPage = config.siteLinkedinPage;
    const siteYouTubePage = config.siteYouTubePage;

    const sameOrganizationAs = [
      facebookPage,
      twitterPage,
      instagramPage,
      linkedInPage,
      siteYouTubePage,
    ].filter(v => v != null);

    // Schema for search engines (helps them to understand what this page is about)
    // http://schema.org
    // We are using JSON-LD format

    // Schema attribute can be either single schema object or an array of objects
    // This makes it possible to include several different items from the same page.
    // E.g. Product, Place, Video
    const schemaFromProps = normalizeSchemaInput(schema);
    const addressMaybe = config.address?.streetAddress ? { address: config.address } : {};
    const schemaArrayJSONString = JSON.stringify([
      ...schemaFromProps,
      // {
      //   '@context': 'https://schema.org',
      //   '@type': 'Organization',
      //   '@id': `${marketplaceRootURL}#organization`,
      //   url: marketplaceRootURL,
      //   name: marketplaceName,
      //   sameAs: sameOrganizationAs,
      //   logo: config.branding.logoImageMobileURL,
      //   ...addressMaybe,
      // },
      // {
      //   '@context': 'https://schema.org',
      //   '@type': 'WebSite',
      //   url: marketplaceRootURL,
      //   description: schemaDescription,
      //   name: schemaTitle,
      // },
    ]);

    const scrollPositionStyles = scrollingDisabled
      ? { marginTop: `${-1 * this.scrollPosition}px` }
      : {};

    // If scrolling is not disabled, but content element has still scrollPosition set
    // in style attribute, we scrollTo scrollPosition.
    const hasMarginTopStyle = this.contentDiv && this.contentDiv.style.marginTop;
    if (!scrollingDisabled && hasMarginTopStyle) {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, this.scrollPosition);
      });
    }

    // We add favicon through hosted configs
    // NOTE: There's no favicon.ico file. This is an imageAsset object which is used together with <meta> tags.
    // TODO: add app icons too
    const faviconAsset = config.branding.favicon;
    const faviconVariants =
      faviconAsset?.type === 'imageAsset' ? Object.values(faviconAsset.attributes.variants) : [];

    return (
      <div className={classes} style={styles} id="page">
        <Helmet
          htmlAttributes={{
            lang: actualLocale, // Use actualLocale instead of intl.locale
          }}
        >
          <title>{pageTitle}</title>
          {referrer ? <meta name="referrer" content={referrer} /> : null}
          {/* robots tag is now handled server-side in renderer.js for consistent domain-based logic */}
          <link rel="canonical" href={canonicalUrl} />
          {altHreflangs.map(({ path, locale }) => (
            <link
              key={`${marketplaceRootURL}${path}`}
              rel="alternate"
              href={`${marketplaceRootURL}${path}`}
              hreflang={locale.toLowerCase()}
            />
          ))}

          {/** Add x-default using English version from altHreflangs */}
          {altHreflangs.find(lang => lang.locale === "en-US") && (
            <link
              rel="alternate"
              href={`${marketplaceRootURL}${altHreflangs.find(lang => lang.locale === "en-US")?.path}`}
              hreflang="x-default"
            />
          )}

          {faviconVariants.map(variant => {
            return (
              <link
                key={`icon_${variant.width}`}
                rel="icon"
                type="image/png"
                sizes={`${variant.width}x${variant.height}`}
                href={variant.url}
              />
            );
          })}
          <meta name="msapplication-config" content={config.branding.browserConfig} />
          <link
            rel="mask-icon"
            href={config.branding.safariPinnedTab}
            color={config.branding.marketplaceColor}
          />
          <link rel="apple-touch-icon" sizes="180x180" href={config.branding.appleTouchIcon} />
          <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta httpEquiv="Content-Language" content={actualLocale} /> {/* Use actualLocale */}
          {metaToHead.map((metaProps, i) => (
            <meta key={i} {...metaProps} />
          ))}
          <script id="page-schema" type="application/ld+json">
            {schemaArrayJSONString.replace(/</g, '\\u003c')}
          </script> 
        </Helmet>
        <CookieConsent config={config} />
        <div
          className={css.content}
          style={scrollPositionStyles}
          ref={c => {
            this.contentDiv = c;
          }}
        >
          {children}
        </div>
      </div>
    );
  }
}

PageComponent.defaultProps = {
  className: null,
  rootClassName: null,
  children: null,
  author: null,
  openGraphType: 'website',
  description: null,
  facebookImages: null,
  twitterImages: null,
  published: null,
  referrer: null,
  schema: null,
  socialSharing: null,
  twitterHandle: null,
  updated: null,
};

PageComponent.propTypes = {
  className: string,
  rootClassName: string,
  children: any,
  scrollingDisabled: bool.isRequired,

  // Handle referrer policy
  referrer: string,

  // SEO related props
  author: string,
  openGraphType: string, // og:type
  description: string, // page description
  facebookImages: arrayOf(
    shape({
      width: number.isRequired,
      height: number.isRequired,
      url: string.isRequired,
    })
  ),
  twitterImages: arrayOf(
    shape({
      width: number.isRequired,
      height: number.isRequired,
      url: string.isRequired,
    })
  ),
  published: string, // article:published_time
  schema: oneOfType([object, array, string]), // http://schema.org
  socialSharing: shape({
    title: string,
    description: string,
    images1200: arrayOf(
      // Page asset file can define this
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
    images600: arrayOf(
      // Page asset file can define this
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
  }),
  title: string, // page title
  twitterHandle: string, // twitter handle
  updated: string, // article:modified_time

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useLocation
  location: shape({
    pathname: string.isRequired,
    search: string.isRequired,
    hash: string.isRequired,
  }).isRequired,
};

const Page = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const location = useLocation();
  const intl = useIntl();
  const { stPagesData } = useSelector(state => state.hostedAssets);

  const styles = usePartnerStyleLoader(config);

  return (
    <PageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      location={location}
      intl={intl}
      stPagesData={stPagesData}
      styles={styles}
      {...props}
    />
  );
};

export default Page;
